import { useMemo } from 'react';
import {
  orderBy,
  type DocumentData,
  type FirestoreError,
  type QueryConstraint,
} from 'firebase/firestore';
import { usePolledResource } from './usePolledResource';
import { apiList } from './restClient';

export interface CollectionRow<T> {
  id: string;
  data: T;
}

export interface CollectionState<T> {
  rows: CollectionRow<T>[];
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Generic collection reader. Ignores Firestore QueryConstraints — every
 * remaining caller lists a small, unfiltered admin collection
 * (hourlyRates/departments/designationCatalog/clients/tags/users). The one
 * filtered caller (client projects) moved to `useClientProjects`, whose
 * server list-scope enforces the clientId filter.
 *
 * The `...constraints` rest-arg is retained for call-site compatibility
 * (callers still pass `orderByName()`/`orderByField(...)`) but is unused.
 */
export function useCollectionSnapshot<T extends DocumentData>(
  path: string,
  ..._constraints: QueryConstraint[]
): CollectionState<T> {
  const { rows, loading, error } = usePolledResource<T & { id: string }>(
    path,
    () => apiList<T & { id: string }>(`/${path}`),
  );
  const mapped = useMemo(
    () => rows.map((r) => ({ id: r.id, data: r as unknown as T })),
    [rows],
  );
  return { rows: mapped, loading, error: error as FirestoreError | null };
}

export const orderByName = () => orderBy('name');
export const orderByField = (field: string) => orderBy(field);
